import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { EnvironmentService } from '../../service/service.module';
import { ClientPreferenceService, ConfigurationService, NgxToasterService } from '../../index';
import { LocalstorageService } from '../localstorage.service';
import { CLIENT_INFO, DCB_CONTEXT_COMMANDS, IS_ONE_ALGO_ENGINE_ENABLED, USER_THREAD_ID } from '@algomusinc/domain';
import { formatDate } from '@angular/common';


@Injectable({
  providedIn: 'root'
})
export class DynamicComponentBuilderService {

  environment = EnvironmentService.environment;

  public messageContextSubject: BehaviorSubject<any> = new BehaviorSubject([]);

  messageInProgress = false;
  private messageContextList: any[] = [];

  constructor(private httpClient: HttpClient,
    private configurationService: ConfigurationService,
    private storage: LocalstorageService,
    private toastr: NgxToasterService,
    private clientPreferenceService: ClientPreferenceService) { }

  get mongoClientId(): string {
    return this.environment.mongoClientId;
  }

  get algocoreApiUrl(): string {

    const isSnowFlakeurl = this.environment.IS_SNOWFLAKE_ENDPOINT;
    return isSnowFlakeurl ? this.environment.AlgocoreApiUrlSnowFlake : this.environment.AlgocoreApiUrlDatabricks;
  }

  get mongoThreadId(): string {
    return this.storage.get(USER_THREAD_ID);
  }

  allowToAskQuestion() {
    if (this.environment.algoCoreMultiAsk) return true;
    else if (this.messageInProgress) {
      this.toastr.warning('Please Wait', "I'm getting an answer for your previous question now.");
      return false;
    }
    else return true;
  }

  setMessageContextList(contexts: any[], command = '') {
    const contextsList = [] as any[];
    contexts.forEach((context: any) => {
      context.value = context.value.toLowerCase();
      let exists = false;
      this.messageContextList.forEach(element => {
        if ((element.value).toLowerCase() == (context.value).toLowerCase()) {
          exists = true;
          if (element.command == DCB_CONTEXT_COMMANDS.SET_CONTEXT) {
            context.display = element.display;
            context.command = element.command;
          };
        }
      });
      if (!exists) {
        context.command = command;
      } else {
        context.command != DCB_CONTEXT_COMMANDS.SET_CONTEXT && (context.command = command);
      }
      contextsList.push(context);
    });
    this.messageContextList = contextsList;
    this.messageContextSubject.next(this.messageContextList);
  }

  setContextCommand(index: number, command = '') {
    this.messageContextList[index].command = command;
    this.messageContextSubject.next(this.messageContextList);
  }

  resetMessageContextList() {
    this.messageContextList = [];
    this.messageContextSubject.next(this.messageContextList);
  }

  setClientUserThreadId() {
    return new Promise((resolve) => {
      if (!this.environment.IS_ONE_ALGO_ENGINE_ENABLED) resolve (true);
      else {
        this.httpClient.get<any>(this.algocoreApiUrl + `/v1/clients/thread_id`).subscribe((res: any) => {
          this.storage.set(USER_THREAD_ID, res.thread_id);
          resolve(true);
        }, (err: any) => {
          resolve(false);
        });
      }
    });
  }

  getMessageValues(payload: any) {
    return this.httpClient.get<any>(this.algocoreApiUrl + `/v2/messages/${payload.messageId}/${payload.displayId}/${payload.toggleDefId}/select?term=${payload.term}&_type=${payload._type}&q=${payload.q}`);
  }

  removeMessageContext(index: number) {
    this.messageContextList.splice(index, 1);
    this.messageContextSubject.next(this.messageContextList);
  }

  sendMessage(obj: any) {
    const body: any = {
      ...obj,
      message_context: [],
    };
    this.messageContextList.forEach(element => {
      (element.command == DCB_CONTEXT_COMMANDS.SET_CONTEXT) && body.message_context.push(element.value);
    });
    return this.httpClient.post<any>(this.algocoreApiUrl + '/v1/messages/send', body);
  }

  retrieveMessage(messageId: any) {
    return this.httpClient.get<any>(this.algocoreApiUrl + '/v2/messages/' + messageId);
  }

  fetchData(messageId: any, dataSourceId: any, obj: any) {
    return this.httpClient.post<any>(this.algocoreApiUrl + '/v2/messages/' + messageId + '/' + dataSourceId + '/data', obj);
  }

  fetchAutocomplete(data: any) {
    return this.httpClient.post<any>(this.algocoreApiUrl + '/v2/messages/autocomplete', data);
  }

  exportAllTableData(childTabId = 0, parentTabId = 0, messageId: any, obj = {}) {
    return this.httpClient.post<any>(this.algocoreApiUrl + '/v2/messages/' + messageId + '/' + parentTabId + '/' + childTabId + '/export/', obj);
  }

  askQuestion(question: String) {
    if (!this.allowToAskQuestion()) return;
    this.messageInProgress = true;
    const selector = {
      menuId: 0,
      name: question,
      selector: 'app-dynamic-component-builder',
      displayName: question,
      module: 'DynamicComponentBuilderModule',
      content: question,
      data: {
        content: question
      }
    };
    this.configurationService.menuRendererSubject.next(selector);
  }

  uploadData(obj = {}) {
    return this.httpClient.post<any>(this.algocoreApiUrl + '/v2/uploads/data', obj);
  }

  askQuestionForForm = (question: string, data_packet = [] as any[]) => {
    return new Promise((resolve) => {
      this.resetMessageContextList();
      this.messageInProgress = true;
      const sendDate = new Date();
      const obj = {
        thread_id: this.mongoThreadId, // golden eye client
        message: question,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        locale: "en-US",
        user_date: formatDate(sendDate, 'yyyy-MM-dd', 'en-US'),
        user_lat: null,
        user_long: null,
        data_packet: data_packet,
        message_context: [] as any[]
      };
      this.messageContextList.forEach(element => {
        (element.command == DCB_CONTEXT_COMMANDS.SET_CONTEXT) && obj.message_context.push(element.value);
      });
      this.sendMessage(obj)
        .subscribe((res: any) => {
          resolve(res.message_id);
        },
          (err: any) => {
            resolve(false);
            this.messageInProgress = false;
          });
    });
  }

  askInvisibleQuestion = (question: string, data_packet = [] as any[]) => {
    return new Promise((resolve) => {
      if (!this.allowToAskQuestion()) return;
      this.messageInProgress = true;
      const sendDate = new Date();
      const obj = {
        thread_id: this.mongoThreadId, // golden eye client
        message: question,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        locale: "en-US",
        user_date: formatDate(sendDate, 'yyyy-MM-dd', 'en-US'),
        user_lat: null,
        user_long: null,
        data_packet: data_packet,
        message_context: [] as any[]
      };
      this.messageContextList.forEach(element => {
        (element.command == DCB_CONTEXT_COMMANDS.SET_CONTEXT) && obj.message_context.push(element.value);
      });
      this.sendMessage(obj)
        .subscribe((res: any) => {
          this.startRetrievingMessages(res.message_id).then((res: any) => {
            if (res.context) {
              res.context.data.forEach((context: any) => {
                let exists = false;
                this.messageContextList.forEach(element => {
                  element.value.toLowerCase();
                  if (element.value.toLowerCase() == context.value.toLowerCase()) {
                    exists = true;
                    if (element.command == DCB_CONTEXT_COMMANDS.SET_CONTEXT) {
                      context.display = element.display;
                      context.command = element.command;
                    };
                  }
                });
                context.command != DCB_CONTEXT_COMMANDS.SET_CONTEXT && (context.command = res.context.command);
                !exists && this.messageContextList.push(context);
                this.messageContextSubject.next(this.messageContextList);
              });
            }
            resolve(res);
          });
        },
          (err: any) => {
            resolve(false);
            this.messageInProgress = false;
          });
    });
  }

  private startRetrievingMessages = (messagId: any) => {
    return new Promise((resolve) => {
      this.retrieveMessage(messagId)
        .subscribe((res: any) => {
          if (res.steps && res.steps.length) {
            setTimeout(() => {
              this.startRetrievingMessages(messagId).then((res: any) => {
                resolve(res);
              });
            }, 500);
          } else {
            res.id && (res.response.id = res.id);
            resolve(res.response);
            this.messageInProgress = false;
          }
        },
          (err: any) => {
            resolve(false);
            this.messageInProgress = false;
          });
    });
  }

  getAllUsers(userTypeId: number) {
    return this.httpClient.get(`${this.environment.AppAccount}/getall?userTypeId=${userTypeId}`);
  }

  sendEmail(obj: { notes: string, subject: string, recipients: any[], type: string, messages: any[] }) {
    return this.httpClient.post(`${this.algocoreApiUrl}/v1/messages/share-bulk/`, obj);
  }

  upvote(messageId: any) {
    return this.httpClient.post(`${this.algocoreApiUrl}/v1/messages/${messageId}/upvote`, null);
  }

  downvote(messageId: any, obj: { description: any, type: string }) {
    return this.httpClient.post(`${this.algocoreApiUrl}/v1/messages/${messageId}/downvote`, obj);
  }

  fetchRegexData() {
    return this.httpClient.get(`${this.algocoreApiUrl}/v1/clients/chat_configs`);
  }

  getQuestionById(qId: any) {
    return this.httpClient.get(`${this.algocoreApiUrl}/v1/messages/shared_questions/${qId}`);
  }

  getAdminDashboard(
    page: number,
    itemsPerPage: number,
    bodyParams:any
  ) {
    let url = `/v1/admin/messages?page=${page}&page_size=${itemsPerPage}`;
    
    return this.httpClient.post(`${this.algocoreApiUrl}${url}`, bodyParams);
  }

}
